import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierClusterMembersByEntityUrl,
    supplierEntityDetailUrl,
    supplierEntitySplitUrl,
    supplierKpiSummaryUrl,
    supplierSpendSummaryUrl,
    supplierSpendTrendUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";

export type RemoveSupplierClusterMemberVariables = {
    entityId: number;
    /** The row's own `s_id` — what `split` addresses; a `ClusterMember.id` is not accepted. */
    sId: string;
    /** Notification copy only — never sent. */
    displayName: string;
};

/**
 * The reason `split` records on the `NonMatchDecision` it persists. Fixed rather than prompted for:
 * the Cluster section's delete is a one-click action in the design, with no reason field anywhere
 * near it — so the honest thing to record is where the decision was made, not a guess at why.
 */
const REMOVE_REASON = "Removed from the cluster in the supplier profile's Cluster section";

export function useRemoveSupplierClusterMemberMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierEntity, DefaultError, RemoveSupplierClusterMemberVariables>({
        mutationFn: async ({entityId, sId}) => {
            const {data} = await queryHttp.post<SupplierEntity>(
                supplierEntitySplitUrl(entityId),
                {s_id: sId, reason: REMOVE_REASON},
            );
            return data;
        },
        onSuccess: (_entity, {entityId, displayName}) => {
            void queryClient.invalidateQueries({queryKey: [supplierClusterMembersByEntityUrl(entityId)]});
            void queryClient.invalidateQueries({queryKey: [supplierEntityDetailUrl(entityId)]});
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('supplier_360/cluster_member');
                },
            });
            // The removed row's spend leaves this cluster (§2.1, §2.22). The header and spend
            // charts stay mounted on this page, so they refetch here. The Transactions tab and the
            // registry chips unmount with their routes and load again when the user opens them.
            void queryClient.invalidateQueries({queryKey: [supplierKpiSummaryUrl(entityId)]});
            void queryClient.invalidateQueries({queryKey: [supplierSpendSummaryUrl(entityId)]});
            void queryClient.invalidateQueries({queryKey: [supplierSpendTrendUrl(entityId)]});
            notificationStore.show(`${displayName} removed from the cluster.`, {
                severity: 'success',
                id: `cluster-member-remove-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entityId}) => {
            notificationStore.show('Could not remove the raw row. Please try again.', {
                severity: 'error',
                id: `cluster-member-remove-error-${entityId}`,
                autoHideDuration: 4000,
            });
        },
    });
}
